import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import NotificationContext from '../../context/notifications/notificationContext';
import FriendContext from '../../context/friends/friendContext';
import './NotificationBell.css';

// Get the API base URL for images
const baseURL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || 'https://pokematch-vslogger.onrender.com')
  : 'http://localhost:5100';

// Helper function to convert relative URLs to absolute URLs
const getFullImageUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${baseURL}${url}`;
};

const NotificationBell = () => {
  const notificationContext = useContext(NotificationContext);
  const friendContext = useContext(FriendContext);
  const location = useLocation();
  
  const { 
    notifications, 
    unreadCount, 
    getNotifications, 
    getUnreadCount, 
    markNotificationsRead, 
    deleteNotifications 
  } = notificationContext;
  
  const { acceptFriendRequest, rejectFriendRequest } = friendContext;
  
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // No longer need to check for sidebar since notification bell is only in header

  // Fetch notifications and unread count on component mount
  useEffect(() => {
    getUnreadCount();
    
    // Poll for new notifications every 30 seconds
    const notificationInterval = setInterval(() => {
      getUnreadCount();
    }, 30000);
    
    return () => clearInterval(notificationInterval);
  }, []);

  // Load notifications when dropdown is opened
  useEffect(() => {
    if (isOpen) {
      getNotifications();
    }
  }, [isOpen]);

  // Handle marking notifications as read
  useEffect(() => {
    if (isOpen && notifications.length > 0) {
      // Get IDs of non-friend-request notifications that are unread
      const readableNotificationIds = notifications
        .filter(n => n.type !== 'friend_request' && !n.isRead)
        .map(n => n._id);
      
      if (readableNotificationIds.length > 0) {
        markNotificationsRead(readableNotificationIds);
      }
    }
  }, [isOpen, notifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleAcceptRequest = async (userId) => {
    try {
      console.log('Accepting friend request from user ID:', userId);
      await acceptFriendRequest(userId);
      toast.success('Friend request accepted');
      
      // Remove the notification from the local state immediately
      const updatedNotifications = notifications.filter(
        notification => !(notification.type === 'friend_request' && notification.from._id === userId)
      );
      
      // Update notification context directly
      notificationContext.clearNotifications();
      notificationContext.getNotifications();
      notificationContext.getUnreadCount();
    } catch (err) {
      console.error('Error accepting friend request:', err);
      toast.error('Failed to accept friend request');
    }
  };

  const handleRejectRequest = async (userId) => {
    try {
      console.log('Rejecting friend request from user ID:', userId);
      await rejectFriendRequest(userId);
      toast.info('Friend request rejected');
      
      // Remove the notification from the local state immediately
      const updatedNotifications = notifications.filter(
        notification => !(notification.type === 'friend_request' && notification.from._id === userId)
      );
      
      // Update notification context directly
      notificationContext.clearNotifications();
      notificationContext.getNotifications();
      notificationContext.getUnreadCount();
    } catch (err) {
      console.error('Error rejecting friend request:', err);
      toast.error('Failed to reject friend request');
    }
  };

  const handleClearNotifications = async () => {
    try {
      // Get IDs of all non-friend-request notifications
      const notificationIds = notifications
        .filter(n => n.type !== 'friend_request')
        .map(n => n._id);
      
      console.log('Clearing notifications with IDs:', notificationIds);
      
      if (notificationIds.length > 0) {
        await deleteNotifications(notificationIds);
        // Force refresh notifications after clearing
        console.log('Notifications cleared, refreshing list');
        await getNotifications();
        // Also update the unread count
        await getUnreadCount();
      }
    } catch (error) {
      console.error('Error clearing notifications:', error);
      toast.error('Failed to clear notifications');
    }
  };

  // Group notifications by type
  const friendRequests = notifications.filter(n => n.type === 'friend_request');
  const otherNotifications = notifications.filter(n => n.type !== 'friend_request');

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button 
        className="notification-bell" 
        onClick={toggleDropdown}
        aria-label="Notifications"
      >
        <i className="fas fa-bell"></i>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>
      
      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {otherNotifications.length > 0 && (
              <button 
                className="clear-all-btn"
                onClick={handleClearNotifications}
              >
                Clear All
              </button>
            )}
          </div>
          
          <div className="notification-content">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                <p>No notifications</p>
              </div>
            ) : (
              <>
                {/* Friend Requests */}
                {friendRequests.length > 0 && (
                  <div className="notification-section">
                    <h4>Friend Requests</h4>
                    {friendRequests.map(notification => (
                      <div 
                        key={notification._id} 
                        className="notification-item friend-request"
                      >
                        <div className="notification-avatar">
                          {notification.from.profilePicture ? (
                            <img 
                              src={getFullImageUrl(notification.from.profilePicture)} 
                              alt={notification.from.username}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/40';
                              }}
                            />
                          ) : (
                            <i className="fas fa-user-circle"></i>
                          )}
                        </div>
                        <div className="notification-content">
                          <p className="notification-message">
                            <strong>
                              {notification.from.name || notification.from.username}
                            </strong> {' '}
                            sent you a friend request
                          </p>
                          <div className="friend-request-actions">
                            <button 
                              className="btn-accept"
                              onClick={() => handleAcceptRequest(notification.from._id)}
                            >
                              Accept
                            </button>
                            <button 
                              className="btn-reject"
                              onClick={() => handleRejectRequest(notification.from._id)}
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Other Notifications */}
                {otherNotifications.length > 0 && (
                  <div className="notification-section">
                    <h4>Recent</h4>
                    {otherNotifications.map(notification => (
                      <div 
                        key={notification._id} 
                        className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                      >
                        <div className="notification-avatar">
                          {notification.from && notification.from.profilePicture ? (
                            <img 
                              src={getFullImageUrl(notification.from.profilePicture)} 
                              alt={notification.from.username}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/40';
                              }}
                            />
                          ) : notification.from ? (
                            <i className="fas fa-user-circle"></i>
                          ) : (
                            <i className="fas fa-info-circle"></i>
                          )}
                        </div>
                        <div className="notification-content">
                          <p className="notification-message">
                            {notification.message}
                          </p>
                          <p className="notification-time">
                            {new Date(notification.date).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="notification-footer">
            <Link to="/friends" onClick={() => setIsOpen(false)}>
              Manage Friends
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;